import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card } from 'react-bootstrap';

import { useApi } from 'landing-page/hooks/useApi';
import { api } from 'landing-page/helpers/api';
import type { ApiResponse } from 'landing-page/helpers/api';
import MainLayout from 'landing-page/components/layouts/MainLayout';
import Section from 'landing-page/components/common/Section';
import Container from 'landing-page/components/common/Container';
import YouTubePlayer from 'landing-page/components/common/YouTubePlayer';
import { BlogDetailPlaceholder } from 'landing-page/pages/Blog/DetailPlaceholder';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

/** Stable reference so useApi(execute) doesn't change every render and cause infinite requests */
const getCOPDetail = (id: string) => api.getCOPDetail(id);

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const BlogDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: response, execute: fetchDetail, loading } = useApi<
    ApiResponse<import('landing-page/helpers/api').COPDetailData>
  >(getCOPDetail);

  const detail = response?.data ?? null;

  useEffect(() => {
    if (id) fetchDetail(id);
  }, [id, fetchDetail]);

  if (loading || (!detail && !response)) {
    return (
      <MainLayout>
        <Section variant="white" id="blog-detail-page" className="mt-15">
          <BlogDetailPlaceholder />
        </Section>
      </MainLayout>
    );
  }

  if (!detail) {
    return (
      <MainLayout>
        <Section variant="white" id="blog-detail-page" className="mt-15">
          <Container className="px-15">
            <div className="text-center py-10">
              <p className="text-muted fs-5">Artikel tidak ditemukan.</p>
              <Link to="/blog" className="btn btn-primary">
                Kembali ke Blog
              </Link>
            </div>
          </Container>
        </Section>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <style>
        {`
          .blog-detail-pdf-container {
            width: 100%;
            margin: 0;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            overflow: hidden;
          }
          @media (min-width: 768px) {
            .blog-detail-pdf-container {
              width: 50%;
              margin: 0 auto;
            }
          }
        `}
      </style>
      <Section variant="white" id="blog-detail-page" className="mt-15" style={{ backgroundColor: '#FAF9F5' }}>
        <Container className="py-10">
          <BlogDetailBreadcrumb
            category={detail.kategori?.nama_kategori}
            title={detail.judul}
          />
        </Container>

        <Container className="pb-10">
          <div className="row">
            <div className="col-md-12">
              <h1
                className="display-5 fw-bold text-primary mb-4"
                style={{ textTransform: 'unset' }}
              >
                {detail.judul}
              </h1>
            </div>
          </div>
        </Container>

        <Container className="pb-10">
          <div className="row">
            <div className="col-md-8">
              <Card className="mb-5 border-0 shadow-sm rounded-4 overflow-hidden">
                {detail.upload_dokumen ? (
                  <BlogDetailPdfViewer fileUrl={detail.upload_dokumen} />
                ) : (
                  <Card.Img
                    src={detail.upload_thumbnail ?? ''}
                    alt="Featured"
                    style={{
                      maxHeight: 350,
                      objectFit: 'cover',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                    }}
                  />
                )}
                <Card.Body>
                  <BlogDetailMetaAndShare
                    trafficCount={detail.traffic_count}
                    createdAt={detail.created_at}
                    id={String(id)}
                    title={detail.judul}
                    onShare={() => id && api.getCOPDetailShare(id)}
                  />
                  <div
                    className="text-gray-800 fs-5"
                    style={{ lineHeight: 1.7 }}
                    dangerouslySetInnerHTML={{ __html: detail.abstrak ?? '' }}
                  />
                  {detail.link_youtube && (
                    <div className="mt-5">
                      <YouTubePlayer key={detail.link_youtube} videoId={detail.link_youtube} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            <div className="col-md-4">
              {detail.related_articles && detail.related_articles.length > 0 && (
                <>
                  <h6 className="fw-bold text-gray-800 mb-5">Artikel Lainnya</h6>
                  {detail.related_articles.map((article, index) => (
                    <Link
                      to={`/blog/${article.id}`}
                      key={`${article.id}-${index}`}
                      className="text-decoration-none"
                    >
                      <Card className="mb-5 border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body>
                          <div className="d-flex align-items-center justify-content-between gap-3">
                            <div className="d-flex align-items-center" style={{ width: '35%' }}>
                              <img
                                src={article.upload_thumbnail}
                                alt={article.judul}
                                style={{
                                  width: '100%',
                                  height: '94px',
                                  objectFit: 'contain',
                                  borderRadius: 8,
                                }}
                              />
                            </div>
                            <div
                              className="d-flex flex-column justify-content-between w-100"
                              style={{ width: '65%', height: '84px' }}
                            >
                              <h6
                                className="card-title mb-0 text-gray-800"
                                style={{
                                  textTransform: 'unset',
                                  fontSize: 16,
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  lineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {article.judul}
                              </h6>
                              <div className="d-flex align-items-center gap-1">
                                <span className="text-gray-800 fw-bold">
                                  {article.traffic_count ?? 0}
                                </span>
                                <span className="text-gray-800">x dilihat</span>
                              </div>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Link>
                  ))}
                </>
              )}
            </div>
          </div>
        </Container>
      </Section>
    </MainLayout>
  );
};

export default BlogDetail;

// --- Sub-components (same UX as CoP Detail) ---

const BlogDetailBreadcrumb: React.FC<{
  category?: string;
  title?: string;
}> = ({ category, title }) => (
  <nav aria-label="breadcrumb">
    <ol className="breadcrumb mb-0" style={{ backgroundColor: 'transparent', padding: 0 }}>
        <li className="breadcrumb-item">
          <Link to="/" className="text-muted text-hover-primary">
            Beranda
          </Link>
        </li>
        <li className="breadcrumb-item">
          <i className="bi bi-chevron-right text-muted" />
        </li>
        <li className="breadcrumb-item">
          <Link to="/blog" className="text-muted text-hover-primary">
            Blog
          </Link>
        </li>
        {category && (
          <>
            <li className="breadcrumb-item">
              <i className="bi bi-chevron-right text-muted" />
            </li>
            <li className="breadcrumb-item">
              <span className="text-muted">{category}</span>
            </li>
          </>
        )}
        <li className="breadcrumb-item">
          <i className="bi bi-chevron-right text-muted" />
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          <span className="text-gray-800 fw-semibold">{title ?? 'Detail'}</span>
        </li>
      </ol>
  </nav>
);

const BlogDetailMetaAndShare: React.FC<{
  trafficCount?: number;
  createdAt?: string;
  id: string;
  title: string;
  onShare: () => void;
}> = ({ trafficCount, createdAt, id, title, onShare }) => {
  const formattedDate = createdAt
    ? new Date(createdAt).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '';
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = (e: React.MouseEvent) => {
    e.preventDefault();
    onShare();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl);
      alert('Link berhasil disalin!');
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-between border-bottom pb-3 mb-3 flex-wrap gap-2">
      <div className="d-flex align-items-center text-muted" style={{ fontSize: 14 }}>
        <span className="text-gray-800 fw-bold">{trafficCount ?? 0}</span>
        <span className="ms-1">x dilihat</span>
        <span className="ms-1">- dibuat pada</span>
        <span className="text-gray-800 fw-bold ms-1">{formattedDate}</span>
      </div>
      <div>
        <span className="me-2 fs-5">Bagikan:</span>
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-light btn-sm me-1"
          onClick={onShare}
          aria-label="Share on Facebook"
        >
          <i className="bi bi-facebook" />
        </a>
        <a
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-light btn-sm me-1"
          onClick={onShare}
          aria-label="Share on Twitter"
        >
          <i className="bi bi-twitter" />
        </a>
        <a
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-light btn-sm me-1"
          onClick={onShare}
          aria-label="Share on LinkedIn"
        >
          <i className="bi bi-linkedin" />
        </a>
        <a
          href="#"
          className="btn btn-light btn-sm"
          onClick={handleCopyLink}
          aria-label="Copy link"
        >
          <i className="bi bi-link-45deg" />
        </a>
      </div>
    </div>
  );
};

const BlogDetailPdfViewer: React.FC<{ fileUrl: string }> = ({ fileUrl }) => {
  const [numPages, setNumPages] = React.useState<number | null>(null);
  const [pageNumber, setPageNumber] = React.useState(1);

  const onDocumentLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  };

  const width = typeof window !== 'undefined' ? (window.innerWidth > 768 ? 600 : window.innerWidth - 60) : 600;

  return (
    <div className="blog-detail-pdf-container">
      <Document
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={<div className="text-center p-5">Memuat PDF...</div>}
        error={<div className="text-center p-5 text-danger">Gagal memuat PDF</div>}
      >
        <Page
          pageNumber={pageNumber}
          width={width}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
      </Document>
      {numPages != null && numPages > 0 && (
        <div className="d-flex justify-content-between align-items-center p-3 bg-light">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => p - 1)}
          >
            <i className="bi bi-chevron-left" /> Sebelumnya
          </button>
          <p className="mb-0">
            Halaman {pageNumber} dari {numPages}
          </p>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => p + 1)}
          >
            Berikutnya <i className="bi bi-chevron-right" />
          </button>
        </div>
      )}
    </div>
  );
};
